/**
 * @file Atom: Icon
 * @description A collection of reusable SVG icon components used throughout the UI.
 * Centralizing icons here makes them easy to find, manage, and use consistently.
 */

import type React from 'react';

export const StarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.007z"
      clipRule="evenodd"
    />
  </svg>
);

export const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

export const XCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
      clipRule="evenodd"
    />
  </svg>
);

export const MagnifyingGlassIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
    />
  </svg>
);

export const ImageIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
    />
  </svg>
);

export const LockIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    {...props}
  >
    <rect x="5" y="11" width="14" height="11" rx="2" fill="currentColor" />
    <path
      d="M8 11V7C8 4.23858 9.79086 2 12 2C14.2091 2 16 4.23858 16 7V11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const LockFillIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path
      d="M32 4C34.1217 4 36.1566 4.84285 37.6569 6.34315C39.1571 7.84344 40 9.87827 40 12V28H24V12C24 9.87827 24.8429 7.84344 26.3431 6.34315C27.8434 4.84285 29.8783 4 32 4V4ZM44 28V12C44 8.8174 42.7357 5.76516 40.4853 3.51472C38.2348 1.26428 35.1826 0 32 0C28.8174 0 25.7652 1.26428 23.5147 3.51472C21.2643 5.76516 20 8.8174 20 12V28C17.8783 28 15.8434 28.8429 14.3431 30.3431C12.8429 31.8434 12 33.8783 12 36V56C12 58.1217 12.8429 60.1566 14.3431 61.6569C15.8434 63.1571 17.8783 64 20 64H44C46.1217 64 48.1566 63.1571 49.6569 61.6569C51.1571 60.1566 52 58.1217 52 56V36C52 33.8783 51.1571 31.8434 49.6569 30.3431C48.1566 28.8429 46.1217 28 44 28V28Z"
      fill="white"
    />
  </svg>
);

export const ArrowRightCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <g clipPath="url(#clip0_1797_21161)">
      <path
        className="arrow-path"
        d="M20 0C25.3043 0 30.3914 2.10714 34.1421 5.85786C37.8929 9.60859 40 14.6957 40 20C40 25.3043 37.8929 30.3914 34.1421 34.1421C30.3914 37.8929 25.3043 40 20 40C14.6957 40 9.60859 37.8929 5.85786 34.1421C2.10714 30.3914 0 25.3043 0 20C0 14.6957 2.10714 9.60859 5.85786 5.85786C9.60859 2.10714 14.6957 0 20 0V0ZM11.25 18.75C10.9185 18.75 10.6005 18.8817 10.3661 19.1161C10.1317 19.3505 10 19.6685 10 20C10 20.3315 10.1317 20.6495 10.3661 20.8839C10.6005 21.1183 10.9185 21.25 11.25 21.25H25.7325L20.365 26.615C20.2488 26.7312 20.1566 26.8692 20.0937 27.021C20.0308 27.1729 19.9984 27.3356 19.9984 27.5C19.9984 27.6644 20.0308 27.8271 20.0937 27.979C20.1566 28.1308 20.2488 28.2688 20.365 28.385C20.4812 28.5012 20.6192 28.5934 20.771 28.6563C20.9229 28.7192 21.0856 28.7516 21.25 28.7516C21.4144 28.7516 21.5771 28.7192 21.729 28.6563C21.8808 28.5934 22.0188 28.5012 22.135 28.385L29.635 20.885C29.7514 20.7689 29.8438 20.6309 29.9068 20.4791C29.9698 20.3272 30.0022 20.1644 30.0022 20C30.0022 19.8356 29.9698 19.6728 29.9068 19.5209C29.8438 19.3691 29.7514 19.2311 29.635 19.115L22.135 11.615C22.0188 11.4988 21.8808 11.4066 21.729 11.3437C21.5771 11.2808 21.4144 11.2484 21.25 11.2484C21.0856 11.2484 20.9229 11.2808 20.771 11.3437C20.6192 11.4066 20.4812 11.4988 20.365 11.615C20.2488 11.7312 20.1566 11.8692 20.0937 12.021C20.0308 12.1729 19.9984 12.3356 19.9984 12.5C19.9984 12.6644 20.0308 12.8271 20.0937 12.979C20.1566 13.1308 20.2488 13.2688 20.365 13.385L25.7325 18.75H11.25Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_1797_21161">
        <rect width="40" height="40" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const ArrowLeftCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <g clipPath="url(#clip0_1797_21162)">
      <path
        className="arrow-path"
        d="M20 40C14.6957 40 9.60859 37.8929 5.85786 34.1421C2.10714 30.3914 0 25.3043 0 20C0 14.6957 2.10714 9.60859 5.85786 5.85786C9.60859 2.10714 14.6957 0 20 0C25.3043 0 30.3914 2.10714 34.1421 5.85786C37.8929 9.60859 40 14.6957 40 20C40 25.3043 37.8929 30.3914 34.1421 34.1421C30.3914 37.8929 25.3043 40 20 40V40ZM28.75 21.25C29.0815 21.25 29.3995 21.1183 29.6339 20.8839C29.8683 20.6495 30 20.3315 30 20C30 19.6685 29.8683 19.3505 29.6339 19.1161C29.3995 18.8817 29.0815 18.75 28.75 18.75H14.2675L19.635 13.385C19.7512 13.2688 19.8434 13.1308 19.9063 12.979C19.9692 12.8271 20.0016 12.6644 20.0016 12.5C20.0016 12.3356 19.9692 12.1729 19.9063 12.021C19.8434 11.8692 19.7512 11.7312 19.635 11.615C19.5188 11.4988 19.3808 11.4066 19.229 11.3437C19.0771 11.2808 18.9144 11.2484 18.75 11.2484C18.5856 11.2484 18.4229 11.2808 18.271 11.3437C18.1192 11.4066 17.9812 11.4988 17.865 11.615L10.365 19.115C10.2486 19.2311 10.1562 19.3691 10.0932 19.5209C10.0302 19.6728 9.99784 19.8356 9.99784 20C9.99784 20.1644 10.0302 20.3272 10.0932 20.4791C10.1562 20.6309 10.2486 20.7689 10.365 20.885L17.865 28.385C17.9812 28.5012 18.1192 28.5934 18.271 28.6563C18.4229 28.7192 18.5856 28.7516 18.75 28.7516C18.9144 28.7516 19.0771 28.7192 19.229 28.6563C19.3808 28.5934 19.5188 28.5012 19.635 28.385C19.7512 28.2688 19.8434 28.1308 19.9063 27.979C19.9692 27.8271 20.0016 27.6644 20.0016 27.5C20.0016 27.3356 19.9692 27.1729 19.9063 27.021C19.8434 26.8692 19.7512 26.7312 19.635 26.615L14.2675 21.25H28.75Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_1797_21162">
        <rect width="40" height="40" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const HintIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <g clipPath="url(#clip0_1797_20352)">
      <path
        d="M29.3558 25.8584C31.7765 22.5551 32.8607 18.4596 32.3915 14.3913C31.9223 10.3229 29.9343 6.58177 26.8252 3.91625C23.7161 1.25072 19.7151 -0.142571 15.6229 0.015112C11.5306 0.172795 7.64874 1.86983 4.75395 4.76669C1.85916 7.66355 0.164906 11.5466 0.0101515 15.639C-0.144603 19.7314 1.25155 23.7313 3.9193 26.8385C6.58704 29.9457 10.3296 31.9311 14.3983 32.3974C18.467 32.8637 22.5617 31.7765 25.8633 29.3534H25.8608C25.9358 29.4534 26.0158 29.5484 26.1058 29.6409L35.7308 39.2659C36.1995 39.735 36.8355 39.9987 37.4986 39.9989C38.1618 39.9992 38.7979 39.7359 39.267 39.2672C39.7361 38.7984 39.9998 38.1625 40 37.4993C40.0003 36.8361 39.737 36.2 39.2683 35.7309L29.6433 26.1059C29.5539 26.0154 29.4578 25.9319 29.3558 25.8559V25.8584ZM30.0008 16.2484C30.0008 18.0541 29.6451 19.8421 28.9541 21.5103C28.2631 23.1785 27.2503 24.6943 25.9735 25.9711C24.6967 27.2479 23.1809 28.2608 21.5127 28.9518C19.8444 29.6428 18.0565 29.9984 16.2508 29.9984C14.4451 29.9984 12.6571 29.6428 10.9889 28.9518C9.32065 28.2608 7.80487 27.2479 6.52806 25.9711C5.25126 24.6943 4.23844 23.1785 3.54744 21.5103C2.85643 19.8421 2.50078 18.0541 2.50078 16.2484C2.50078 12.6017 3.94944 9.10433 6.52806 6.5257C9.10669 3.94708 12.6041 2.49842 16.2508 2.49842C19.8975 2.49842 23.3949 3.94708 25.9735 6.5257C28.5521 9.10433 30.0008 12.6017 30.0008 16.2484Z"
        fill="black"
      />
      <rect
        x="15"
        y="6.66663"
        width="3.33333"
        height="13.3333"
        rx="1.66667"
        fill="black"
      />
      <rect
        x="15"
        y="23.3333"
        width="3.33333"
        height="3.33333"
        rx="1.66667"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="clip0_1797_20352">
        <rect width="40" height="40" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const BackHomeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    {/* Left arrow */}
    <path
      d="M4 12H14M4 12L9 7M4 12L9 17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Vertical line on right */}
    <path
      d="M18 5V19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const PlayIconCircle: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);

export const PlayIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path d="M72.475 54.3549L32.7062 77.4299C29.3312 79.3861 25 77.0174 25 73.0736V26.9236C25 22.9861 29.325 20.6111 32.7062 22.5736L72.475 45.6486C73.2427 46.0869 73.8809 46.7204 74.3248 47.4849C74.7687 48.2494 75.0025 49.1177 75.0025 50.0018C75.0025 50.8858 74.7687 51.7541 74.3248 52.5186C73.8809 53.2831 73.2427 53.9166 72.475 54.3549V54.3549Z" />
  </svg>
);

export const GearIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path
      d="M21.6002 14.4H18.9842C18.7802 15.24 18.4562 16.02 18.0122 16.716L19.8602 18.564L17.3402 21.084L15.4922 19.236C14.7962 19.668 14.0162 19.992 13.2002 20.184V22.8H9.60019V20.184C8.78419 19.992 8.0042 19.668 7.3082 19.236L5.4602 21.084L2.9162 18.54L4.7642 16.692C4.3322 15.996 4.0082 15.216 3.8162 14.4H1.2002V10.836H3.8042C3.9962 9.99602 4.3322 9.21602 4.7642 8.50802L2.9162 6.66002L5.4362 4.14002L7.28419 5.98802C7.9802 5.54402 8.77219 5.22002 9.60019 5.01602V2.40002H13.2002V5.01602C14.0162 5.20802 14.7962 5.53202 15.4922 5.96402L17.3402 4.11602L19.8842 6.66002L18.0362 8.50802C18.4682 9.21602 18.8042 9.99602 18.9962 10.836H21.6002V14.4ZM11.4002 16.2C13.3922 16.2 15.0002 14.592 15.0002 12.6C15.0002 10.608 13.3922 9.00002 11.4002 9.00002C9.40819 9.00002 7.80019 10.608 7.80019 12.6C7.80019 14.592 9.40819 16.2 11.4002 16.2Z"
      fill="currentColor"
    />
  </svg>
);

export const ChevronLeftIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckboxIcon: React.FC<React.SVGProps<SVGSVGElement>> = (
  props,
) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <mask id="checkmark-cutout-icon">
      <rect width="24" height="24" fill="white" />
      <path
        d="M7 12L10.5 15.5L17 9"
        stroke="black"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </mask>
    <rect
      x="2"
      y="2"
      width="20"
      height="20"
      rx="4"
      fill="#C5E8E8"
      mask="url(#checkmark-cutout-icon)"
    />
  </svg>
);
